import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";

const ChangePassword = ({ username, role, onPasswordChangeSuccess }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(null); 

  const backendURL = process.env.REACT_APP_BACKEND_URL;
  const { getAuthHeaders } = useAuth();

  // Beautiful popup function
  const showPopup = (message, type = "success") => {
    setPopup({ message, type });
    setTimeout(() => setPopup(null), 2500);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!oldPassword || !newPassword || !confirmPassword) {
      showPopup("⚠️ All fields are required.", "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      showPopup("❌ New password and confirm password do not match.", "error");
      return;
    }

    try {
      setLoading(true);

      // ✅ Include JWT header
      const res = await axios.put(
        `${backendURL}/auth/change-password`,
        {
          username,
          old_password: oldPassword,
          new_password: newPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(), // ✅ attaches { Authorization: "Bearer <token>" }
          },
        }
      );

      // ✅ Success popup
      showPopup(res.data.message || "✅ Password changed successfully!", "success");

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");

      if (onPasswordChangeSuccess) onPasswordChangeSuccess();
    } catch (err) {
      const msg = err.response?.data?.detail || "❌ Failed to change password.";
      showPopup(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Popup (beautiful, centered fade-in-out) */}
      {popup && (
        <div
          className={`fixed top-6 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg text-white text-lg font-medium transition-all duration-300 z-[9999] ${
            popup.type === "success"
              ? "bg-green-600 shadow-green-400/40"
              : "bg-red-600 shadow-red-400/40"
          } animate-fade-in`}
        >
          {popup.message}
        </div>
      )}

      {/* Main modal */}
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50">
        <div className="bg-[#1c1c1e] text-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-yellow-600 relative">
          {/* Close button */}
          <button
            onClick={onPasswordChangeSuccess}
            className="absolute top-3 right-3 text-gray-300 hover:text-yellow-600 transition"
          >
            ✕
          </button>

          <h2 className="text-2xl font-semibold mb-5 text-center text-indigo-400">
            Change Password for <span className="text-yellow-400">{username}</span> ({role})
          </h2>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Old Password
              </label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full p-2 rounded-md bg-[#2a2a2d] border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="Enter old password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-2 rounded-md bg-[#2a2a2d] border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="Enter new password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-2 rounded-md bg-[#2a2a2d] border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="Confirm new password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 text-white rounded-md font-semibold transition duration-300 ${
                loading
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-yellow-600"
              }`}
            >
              {loading ? "Changing..." : "Change Password"}
            </button>
          </form>
        </div>
      </div>

      {/* Fade animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
      `}</style>
    </>
  );
};

export default ChangePassword;
